/**
 * phanHeStorage — phân hệ đang mở (session, không persist TOPPION:auth).
 *
 * Logic: route /van-hoa|/quy-trinh suy PH; shared/intro/login → null (giữ storage);
 * mặc định BSC. Event phanHe:changed đồng bộ same-tab.
 *
 * Hàm chính:
 * - resolvePhanHeFromRoute / resolveProfilePhanHe
 * - getPhanHeFromStorage / setPhanHeToStorage / clearPhanHeFromStorage / subscribePhanHeStorage
 * - convertPhanHeToNumber (0/1/2)
 */
import { PHAN_HE_STORAGE_KEY } from '@/states/authen';
import { CONSTANTS } from '@/utils/constants';

export const PHAN_HE_CHANGED_EVENT = 'phanHe:changed';

type PhanHeChangedDetail = {
    value: string | null;
};

const normalizeRoutePath = (value?: string | null) => {
    const raw = String(value || '').split('?')[0].split('#')[0].toLowerCase();
    // next.config trailingSlash: true → asPath/location là `/role-group/`
    if (raw.length > 1 && raw.endsWith('/')) return raw.slice(0, -1);
    return raw;
};

const isIgnoredRoutePath = (path: string) =>
    !path
    || path === '/404'
    || path.startsWith('/_error')
    || path === '/500';

const isAuthOrIntroPath = (path: string) =>
    path === '/intro'
    || path.startsWith('/login')
    || path.startsWith('/logout')
    || path.startsWith('/idle');

/**
 * Route dùng chung giữa các phân hệ (BSC / VH / QT).
 * Không suy ra phân hệ từ URL — giữ phanHe đang lưu (storage/recoil).
 * VH đã có prefix /van-hoa/... nên không vào nhánh này.
 */
const isSharedCrossModulePath = (path: string) =>
    path === '/thiet-lap/cau-hinh'
    || path.startsWith('/to-chuc/')
    || path === '/role-group'
    || path === '/role-approve'
    || path === '/thiet-lap/don-vi-do-luong'
    || path === '/thiet-lap/api-sync-data'
    || path === '/thiet-lap/api-co-cau'
    || path.startsWith('/thiet-lap/api-co-cau/')
    || path === '/thiet-lap/api-url-whitelist'
    || path === '/thiet-lap/languages'
    || path === '/thiet-lap/resources'
    || path === '/thiet-lap/license';

const collectRoutePathCandidates = (pathname?: string | null, asPath?: string | null): string[] => {
    const paths: string[] = [];
    const push = (value?: string | null) => {
        const path = normalizeRoutePath(value);
        if (path && !paths.includes(path)) paths.push(path);
    };

    push(asPath);
    push(pathname);
    if (typeof window !== 'undefined') {
        push(window.location.pathname);
    }
    return paths;
};

/** Ưu tiên URL thực (asPath) — tránh nhầm BSC khi Next trả pathname `/404`. */
export function resolvePhanHeFromRoute(
    pathname?: string | null,
    asPath?: string | null,
): string | null {
    const candidates = collectRoutePathCandidates(pathname, asPath);

    for (const path of candidates) {
        if (isIgnoredRoutePath(path)) continue;
        if (path.startsWith('/van-hoa')) return CONSTANTS.PHAN_HE.VAN_HOA;
        if (path.startsWith('/quy-trinh')) return CONSTANTS.PHAN_HE.QUY_TRINH;
    }

    for (const path of candidates) {
        if (isIgnoredRoutePath(path)) continue;
        // Route dùng chung: giữ phân hệ hiện tại (vd. đang QT mở Cơ cấu tổ chức → không nhảy sang BSC)
        if (isSharedCrossModulePath(path) || isAuthOrIntroPath(path)) return null;
        return CONSTANTS.PHAN_HE.BSC;
    }

    return null;
}

/** Phân hệ đang mở — sessionStorage (không persist localStorage / auth). */
export function getPhanHeFromStorage(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        return sessionStorage.getItem(PHAN_HE_STORAGE_KEY)
            // migrate 1 lần từ localStorage cũ nếu session chưa có
            ?? (() => {
                const legacy = localStorage.getItem(PHAN_HE_STORAGE_KEY);
                if (legacy) {
                    sessionStorage.setItem(PHAN_HE_STORAGE_KEY, legacy);
                    localStorage.removeItem(PHAN_HE_STORAGE_KEY);
                }
                return legacy;
            })();
    } catch {
        return null;
    }
}

export function resolvePhanHeFromPathname(
    pathname: string | null | undefined,
    asPath?: string | null,
): string | null {
    return resolvePhanHeFromRoute(pathname, asPath);
}

/** Phân hệ dùng khi gọi API profile — ưu tiên route, rồi sessionStorage, mặc định BSC. */
export function resolveProfilePhanHe(pathname?: string | null, asPath?: string | null): string {
    return resolvePhanHeFromRoute(pathname, asPath) || getPhanHeFromStorage() || CONSTANTS.PHAN_HE.BSC;
}

/**
 * Set `phanHe` in sessionStorage and notify same-tab listeners.
 * Note: the native `storage` event does NOT fire in the same tab / across sessionStorage tabs.
 */
export function setPhanHeToStorage(value: string) {
    if (typeof window === 'undefined') return;
    try {
        if (sessionStorage.getItem(PHAN_HE_STORAGE_KEY) === value) return;
        sessionStorage.setItem(PHAN_HE_STORAGE_KEY, value);
        // dọn localStorage legacy nếu còn
        localStorage.removeItem(PHAN_HE_STORAGE_KEY);
    } catch {
        // ignore quota / private mode
    }
    window.dispatchEvent(new CustomEvent<PhanHeChangedDetail>(PHAN_HE_CHANGED_EVENT, { detail: { value } }));
}

export function clearPhanHeFromStorage() {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.removeItem(PHAN_HE_STORAGE_KEY);
        localStorage.removeItem(PHAN_HE_STORAGE_KEY);
    } catch {
        // ignore
    }
}

export function subscribePhanHeStorage(onChange: (value: string | null) => void) {
    if (typeof window === 'undefined') return () => { };

    const onCustom = (e: Event) => {
        const ce = e as CustomEvent<PhanHeChangedDetail>;
        onChange(ce.detail?.value ?? getPhanHeFromStorage());
    };

    // sessionStorage không share cross-tab — chỉ listen custom event same-tab
    window.addEventListener(PHAN_HE_CHANGED_EVENT, onCustom);

    return () => {
        window.removeEventListener(PHAN_HE_CHANGED_EVENT, onCustom);
    };
}

export function convertPhanHeToNumber(phanHe: string): number {
    if (phanHe === 'vanhoa') return 1;
    if (phanHe === 'bsc') return 0;
    if (phanHe === 'quytrinh') return 2;
    return 0;
}
